import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { usePlatform } from '../model/demo-context';
import type { PartnerSender } from '../model/types';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Remetente padrão dos e-mails do sistema e o de cada parceiro white label. */
export function SenderTab() {
  const { config, saveSender } = usePlatform();
  const [fromEmail, setFromEmail] = useState(config.sender.fromEmail);
  const [fromName, setFromName] = useState(config.sender.fromName);
  const [editing, setEditing] = useState<PartnerSender | 'new' | null>(null);

  useEffect(() => {
    setFromEmail(config.sender.fromEmail);
    setFromName(config.sender.fromName);
  }, [config.sender.fromEmail, config.sender.fromName]);

  const dirty = fromEmail !== config.sender.fromEmail || fromName !== config.sender.fromName;
  const invalid = !EMAIL_RE.test(fromEmail.trim()) || !fromName.trim();

  const saveDefault = () => {
    saveSender({ ...config.sender, fromEmail: fromEmail.trim(), fromName: fromName.trim() });
    toast.success('Remetente salvo.');
  };

  const savePartner = (partner: PartnerSender, previous?: string) => {
    const others = config.sender.partners.filter((item) => item.partner !== (previous ?? partner.partner));
    saveSender({ ...config.sender, partners: [...others, partner].sort((a, b) => a.partner.localeCompare(b.partner)) });
    toast.success('Parceiro salvo.');
    setEditing(null);
  };

  const removePartner = (name: string) => {
    saveSender({ ...config.sender, partners: config.sender.partners.filter((item) => item.partner !== name) });
    toast.success('Parceiro removido.');
    setEditing(null);
  };

  return (
    <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      <section className="overflow-hidden rounded-2xl border border-border/60 bg-card">
        <h3 className="border-b border-border/60 px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Remetente padrão
        </h3>
        <div className="space-y-3 p-4">
          <div className="space-y-1.5">
            <Label htmlFor="sender-name">Nome</Label>
            <Input id="sender-name" value={fromName} onChange={(e) => setFromName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sender-email">E-mail</Label>
            <Input id="sender-email" type="email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} aria-invalid={invalid || undefined} />
          </div>
          <div className="flex justify-end">
            <Button type="button" disabled={!dirty || invalid} onClick={saveDefault}>
              Salvar
            </Button>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-border/60 bg-card">
        <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-2.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Parceiros</h3>
          <Button type="button" size="sm" variant="secondary" onClick={() => setEditing('new')}>
            <Plus /> Parceiro
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Parceiro</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>E-mail</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {config.sender.partners.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={3} className="py-8 text-center text-sm text-muted-foreground">
                  Sem parceiro: todos usam o remetente padrão.
                </TableCell>
              </TableRow>
            ) : (
              config.sender.partners.map((partner) => (
                <TableRow key={partner.partner} className="cursor-pointer" onClick={() => setEditing(partner)} tabIndex={0}>
                  <TableCell className="font-mono text-xs">{partner.partner}</TableCell>
                  <TableCell className="text-sm">{partner.fromName}</TableCell>
                  <TableCell className="text-sm">{partner.fromEmail}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </section>

      <PartnerDialog
        target={editing}
        onClose={() => setEditing(null)}
        onSave={savePartner}
        onRemove={removePartner}
        taken={config.sender.partners.map((item) => item.partner)}
      />
    </div>
  );
}

function PartnerDialog({
  target,
  onClose,
  onSave,
  onRemove,
  taken,
}: {
  target: PartnerSender | 'new' | null;
  onClose: () => void;
  onSave: (partner: PartnerSender, previous?: string) => void;
  onRemove: (name: string) => void;
  taken: string[];
}) {
  const [partner, setPartner] = useState('');
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');

  useEffect(() => {
    if (!target) return;
    const current = target === 'new' ? null : target;
    setPartner(current?.partner ?? '');
    setFromName(current?.fromName ?? '');
    setFromEmail(current?.fromEmail ?? '');
  }, [target]);

  const previous = target && target !== 'new' ? target.partner : undefined;
  const key = partner.trim().toLowerCase();
  const duplicate = key !== previous && taken.includes(key);
  const invalid = !key || duplicate || !fromName.trim() || !EMAIL_RE.test(fromEmail.trim());

  return (
    <Dialog open={Boolean(target)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{previous ? `Parceiro ${previous}` : 'Novo parceiro'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="partner-key">Identificador</Label>
            <Input id="partner-key" value={partner} onChange={(e) => setPartner(e.target.value)} placeholder="goomer" className="font-mono" aria-invalid={duplicate || undefined} />
            {duplicate ? <p className="text-xs text-destructive">Já existe.</p> : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="partner-name">Nome</Label>
            <Input id="partner-name" value={fromName} onChange={(e) => setFromName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="partner-email">E-mail</Label>
            <Input id="partner-email" type="email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} />
          </div>
        </div>
        <DialogFooter className="gap-2">
          {previous ? (
            <Button type="button" variant="ghost" className="mr-auto text-destructive" onClick={() => onRemove(previous)}>
              <Trash2 /> Remover
            </Button>
          ) : null}
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={invalid}
            onClick={() => onSave({ partner: key, fromName: fromName.trim(), fromEmail: fromEmail.trim() }, previous)}
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
